/**
 * Advanced Color Picker Component
 * Provides a 2D gradient selector, hue slider, and RGB inputs
 */
class ColorPicker {
    constructor(containerId, initialColor = '#4169E1', onChange = null) {
        this.container = document.getElementById(containerId);
        this.currentColor = initialColor;
        this.onChange = onChange;
        this.hue = 0;
        this.saturation = 100;
        this.lightness = 50;

        this.init();
    }

    init() {
        this.render();
        this.attachEventListeners();
        this.setColorFromHex(this.currentColor);
    }

    render() {
        SafeHTML.set(this.container, `
            <div class="color-picker-container">
                <!-- 2D Gradient Area -->
                <div class="color-picker-gradient" id="gradient-area">
                    <div class="color-picker-gradient-overlay"></div>
                    <div class="color-picker-cursor" id="gradient-cursor"></div>
                </div>

                <!-- Controls Row -->
                <div class="color-picker-controls">
                    <!-- Color Preview -->
                    <div class="color-picker-preview" id="color-preview"></div>

                    <!-- Hue Slider -->
                    <div class="color-picker-hue-wrapper">
                        <input type="range" 
                               class="color-picker-hue-slider" 
                               id="hue-slider" 
                               min="0" 
                               max="360" 
                               value="0">
                    </div>

                    <!-- RGB Inputs -->
                    <div class="color-picker-rgb">
                        <div class="color-picker-rgb-input">
                            <input type="number" id="rgb-r" min="0" max="255" value="0">
                            <label>R</label>
                        </div>
                        <div class="color-picker-rgb-input">
                            <input type="number" id="rgb-g" min="0" max="255" value="0">
                            <label>G</label>
                        </div>
                        <div class="color-picker-rgb-input">
                            <input type="number" id="rgb-b" min="0" max="255" value="0">
                            <label>B</label>
                        </div>
                    </div>

                    <!-- Eyedropper Icon (decorative for now) -->
                    <button type="button" class="color-picker-eyedropper" title="Selector de color">
                        <i class="fas fa-eyedropper"></i>
                    </button>
                </div>
            </div>
        `);
    }

    attachEventListeners() {
        const gradientArea = document.getElementById('gradient-area');
        const gradientCursor = document.getElementById('gradient-cursor');
        const hueSlider = document.getElementById('hue-slider');
        const rgbR = document.getElementById('rgb-r');
        const rgbG = document.getElementById('rgb-g');
        const rgbB = document.getElementById('rgb-b');

        // Gradient area interaction
        let isDragging = false;

        const updateGradientPosition = (e) => {
            const rect = gradientArea.getBoundingClientRect();
            let x = e.clientX - rect.left;
            let y = e.clientY - rect.top;

            // Clamp values
            x = Math.max(0, Math.min(x, rect.width));
            y = Math.max(0, Math.min(y, rect.height));

            // Update cursor position
            gradientCursor.style.left = `${x}px`;
            gradientCursor.style.top = `${y}px`;

            // Calculate saturation and lightness
            this.saturation = (x / rect.width) * 100;
            this.lightness = 100 - (y / rect.height) * 100;

            this.updateColor();
        };

        gradientArea.addEventListener('mousedown', (e) => {
            isDragging = true;
            updateGradientPosition(e);
        });

        document.addEventListener('mousemove', (e) => {
            if (isDragging) {
                updateGradientPosition(e);
            }
        });

        document.addEventListener('mouseup', () => {
            isDragging = false;
        });

        // Hue slider
        hueSlider.addEventListener('input', (e) => {
            this.hue = parseInt(e.target.value);
            this.updateGradientBackground();
            this.updateColor();
        });

        // RGB inputs
        const updateFromRGB = () => {
            const r = parseInt(rgbR.value) || 0;
            const g = parseInt(rgbG.value) || 0;
            const b = parseInt(rgbB.value) || 0;

            const hex = this.rgbToHex(r, g, b);
            this.setColorFromHex(hex);
        };

        rgbR.addEventListener('input', updateFromRGB);
        rgbG.addEventListener('input', updateFromRGB);
        rgbB.addEventListener('input', updateFromRGB);
    }

    updateGradientBackground() {
        const gradientArea = document.getElementById('gradient-area');
        const hueColor = `hsl(${this.hue}, 100%, 50%)`;
        gradientArea.style.background = hueColor;
    }

    updateColor() {
        const color = `hsl(${this.hue}, ${this.saturation}%, ${this.lightness}%)`;
        const hex = this.hslToHex(this.hue, this.saturation, this.lightness);
        const rgb = this.hexToRgb(hex);

        // Update preview
        const preview = document.getElementById('color-preview');
        preview.style.backgroundColor = hex;

        // Update RGB inputs
        document.getElementById('rgb-r').value = rgb.r;
        document.getElementById('rgb-g').value = rgb.g;
        document.getElementById('rgb-b').value = rgb.b;

        this.currentColor = hex;

        // Trigger onChange callback
        if (this.onChange) {
            this.onChange(hex);
        }
    }

    setColorFromHex(hex) {
        const rgb = this.hexToRgb(hex);
        const hsl = this.rgbToHsl(rgb.r, rgb.g, rgb.b);

        this.hue = hsl.h;
        this.saturation = hsl.s;
        this.lightness = hsl.l;

        // Update UI
        document.getElementById('hue-slider').value = this.hue;
        this.updateGradientBackground();

        // Update gradient cursor position
        const gradientArea = document.getElementById('gradient-area');
        const rect = gradientArea.getBoundingClientRect();
        const x = (this.saturation / 100) * rect.width;
        const y = (1 - this.lightness / 100) * rect.height;

        const cursor = document.getElementById('gradient-cursor');
        cursor.style.left = `${x}px`;
        cursor.style.top = `${y}px`;

        this.updateColor();
    }

    getColor() {
        return this.currentColor;
    }

    // Color conversion utilities
    hexToRgb(hex) {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : { r: 0, g: 0, b: 0 };
    }

    rgbToHex(r, g, b) {
        return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
    }

    rgbToHsl(r, g, b) {
        r /= 255;
        g /= 255;
        b /= 255;

        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        let h, s, l = (max + min) / 2;

        if (max === min) {
            h = s = 0;
        } else {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

            switch (max) {
                case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
                case g: h = ((b - r) / d + 2) / 6; break;
                case b: h = ((r - g) / d + 4) / 6; break;
            }
        }

        return {
            h: Math.round(h * 360),
            s: Math.round(s * 100),
            l: Math.round(l * 100)
        };
    }

    hslToHex(h, s, l) {
        s /= 100;
        l /= 100;

        const c = (1 - Math.abs(2 * l - 1)) * s;
        const x = c * (1 - Math.abs((h / 60) % 2 - 1));
        const m = l - c / 2;
        let r = 0, g = 0, b = 0;

        if (0 <= h && h < 60) {
            r = c; g = x; b = 0;
        } else if (60 <= h && h < 120) {
            r = x; g = c; b = 0;
        } else if (120 <= h && h < 180) {
            r = 0; g = c; b = x;
        } else if (180 <= h && h < 240) {
            r = 0; g = x; b = c;
        } else if (240 <= h && h < 300) {
            r = x; g = 0; b = c;
        } else if (300 <= h && h < 360) {
            r = c; g = 0; b = x;
        }

        const rVal = Math.round((r + m) * 255);
        const gVal = Math.round((g + m) * 255);
        const bVal = Math.round((b + m) * 255);

        return this.rgbToHex(rVal, gVal, bVal);
    }
}

// Make it globally available
window.ColorPicker = ColorPicker;
